import { useStrings } from "features/i18n/useStrings";
import { I18nComponent } from "react/utils/I18nComponent";
import {
    GuxButton,
    GuxFormFieldDropdown,
    GuxIcon,
    GuxInlineAlert,
    GuxTooltip,
} from "genesys-spark-components-react";
import strings from "./SideDrawer.strings";
import { SideDrawerMode } from "enums/SideDrawerMode";
import { Card } from "features/common/Card";
import { TextLine } from "features/common/TextLine";
import { AssignModeButtonSet } from "features/qualityForm/formViewer/footer/ButtonSets";
import "./SideDrawer.less";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    scorableSliceActions,
    selectAssigneeName,
    selectHasAssigneePermissionError,
    selectHasInsufficientPermissionErrorAsAssigner,
    selectHasUpdatePermissionsError,
    selectIsEvaluationIncomplete,
    selectIsEvaluationNotAssigned,
    UNASSIGNED,
} from "./scorableSlice";
import { selectUser } from "features/auth/currentUserSlice";
import SearchDropdown, {
    createSearchResult,
    SearchResult,
} from "features/qualityForm/formEditor/sidePanel/SearchDropdown";
import {
    fetchUsers,
    selectListedUsers,
    usersSliceActions,
} from "features/users/usersSlice";
import { ButtonGroup } from "components/ButtonGroup";
import { CommentEditor, PrivateComment } from "components/Comments/Comments";
import { ErrorStatement } from "features/qualityForm/formEditor/common/ErrorStatement/ErrorStatement";

export function SideDrawer({
    setSideDrawerMode,
}: {
    setSideDrawerMode: React.Dispatch<React.SetStateAction<SideDrawerMode>>;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const listedUsers = useAppSelector(selectListedUsers);
    const currentUser = useAppSelector(selectUser);
    const previousAssigneeName =
        useAppSelector(selectAssigneeName) ?? t.placeholderText.unknown;

    const hasAssigneePermissionError = useAppSelector(
        selectHasAssigneePermissionError
    );
    const hasUpdatePermissionError = useAppSelector(
        selectHasUpdatePermissionsError
    );
    const hasInsufficientPermissionErrorAsAssigner = useAppSelector(
        selectHasInsufficientPermissionErrorAsAssigner
    );

    const [currentAssigneeUser, setCurrentAssigneeUser] = useState<
        SearchResult | undefined
    >(undefined);

    function onSelectedUser(assigneeUser: SearchResult | undefined) {
        setCurrentAssigneeUser(assigneeUser);
    }

    const evaluationNotAssigned = useAppSelector(selectIsEvaluationNotAssigned);

    const dropDownLabel = (
        <>
            <div className="dropdown-label-start">
                <div className="assignee-label">{t.labels.assignee}</div>
                <GuxIcon
                    id="dropdown-alert-info"
                    iconName="alert-info"
                    screenreaderText={"alert-info"}
                ></GuxIcon>
                <GuxTooltip for="dropdown-alert-info">
                    {t.tooltip.assignee}
                </GuxTooltip>
            </div>
        </>
    );

    function onPresetAssign(assignee) {
        onSelectedUser(assignee);
        dispatch(
            usersSliceActions.removeSelectedUser({
                assigneeUser: currentAssigneeUser,
            })
        );
        dispatch(
            usersSliceActions.appendSelectedUser({
                assigneeUser: assignee,
            })
        );
    }

    useEffect(() => {
        dispatch(scorableSliceActions.setUpdateEvaluationError({}));
    }, [currentAssigneeUser, dispatch]);

    const isEvaluationIncomplete = useAppSelector(selectIsEvaluationIncomplete);
    const assignOptionsButtonGroup = (
        <>
            <GuxButton
                accent="tertiary"
                onClick={() => {
                    onPresetAssign(
                        createSearchResult(
                            currentUser?.name,
                            currentUser?.id,
                            currentUser?.version
                        )
                    );
                }}
            >
                {t.labels.assignToMe}
            </GuxButton>
            {!isEvaluationIncomplete && (
                <GuxButton
                    accent="tertiary"
                    onClick={() => {
                        onPresetAssign(
                            createSearchResult(
                                t.placeholderText.userNotAssigned,
                                UNASSIGNED
                            )
                        );
                    }}
                >
                    {t.labels.unassign}
                </GuxButton>
            )}
        </>
    );

    const searchUser = (input) => {
        dispatch(fetchUsers(input)).then(() => {
            dispatch(
                usersSliceActions.appendSelectedUser({
                    assigneeUser: currentAssigneeUser,
                })
            );
        });
    };
    const onExpanded = (input) => {
        if (input) {
            searchUser(input);
        }
    };

    return (
        <div className="SideDrawer HeaderIncluded">
            <Card className="header filled">
                <h3 className="assign-evaluation-panel-title">
                    {t.header.assignEvaluation}
                </h3>
            </Card>
            <Card className="body">
                <div className="BannerSection">
                    {hasInsufficientPermissionErrorAsAssigner && (
                        <GuxInlineAlert accent="error">
                            {t.errors.assignerHasNoAssignPermission}
                        </GuxInlineAlert>
                    )}
                    {hasUpdatePermissionError && (
                        <GuxInlineAlert accent="error">
                            {t.errors.assignerHasNoUpdatePermissions}
                        </GuxInlineAlert>
                    )}
                    <GuxInlineAlert
                        accent="info"
                        data-testid="side-drawer-info-banner"
                    >
                        <I18nComponent
                            i18nKey="SideDrawer.banner.infoMessage"
                            substitutions={{
                                Strong: <strong></strong>,
                            }}
                        ></I18nComponent>
                    </GuxInlineAlert>
                </div>

                <div className="current-assignee-label">
                    {t.labels.currentAssignee}
                </div>
                <div className="current-assignee-value">
                    {evaluationNotAssigned
                        ? t.placeholderText.userNotAssigned
                        : previousAssigneeName}
                </div>
                <TextLine />
                <GuxFormFieldDropdown
                    className="assignee-dropdown"
                    label-position="above"
                >
                    <label slot="label">{dropDownLabel}</label>
                    <SearchDropdown
                        onSearch={searchUser}
                        onSelection={(input) => {
                            dispatch(
                                usersSliceActions.removeSelectedUser({
                                    assigneeUser: currentAssigneeUser,
                                })
                            );
                            onSelectedUser(input);
                        }}
                        selected={currentAssigneeUser}
                        results={Object.values(listedUsers).map((user) =>
                            createSearchResult(
                                user?.name,
                                user?.id,
                                user?.version
                            )
                        )}
                        onExpanded={onExpanded}
                        hasError={hasAssigneePermissionError}
                    />
                    {hasAssigneePermissionError && (
                        <ErrorStatement
                            errorMessage={
                                t.errors.assigneeHasNoNecessaryPermission
                            }
                        />
                    )}
                </GuxFormFieldDropdown>
                <ButtonGroup buttonGroupStart={assignOptionsButtonGroup} />
                <PrivateComment withEditor={CommentEditor.OPEN} />
            </Card>
            <Card className="footer">
                <AssignModeButtonSet
                    setSideDrawerMode={setSideDrawerMode}
                    currentAssignee={{
                        name: currentAssigneeUser?.name ?? "",
                        id: currentAssigneeUser?.value ?? "",
                        version: currentAssigneeUser?.version ?? -1,
                    }}
                    onSelectedUser={onSelectedUser}
                ></AssignModeButtonSet>
            </Card>
        </div>
    );
}
